// Developer: Ankit Gupta
/**
 * What the signed-in person may do, from one call: GET /access/me.
 *
 * The rail, the route guards and page controls ask `can('runs.use')` instead of reading the
 * Keycloak roles, so a company's people see the sections their project memberships open and
 * nothing that would only refuse them. The server derives the list from the same facts the
 * API checks (backend/app/core/access.py); this hook only carries it.
 *
 * If the call fails (the API down, an older server without the endpoint) the menu falls back
 * to what it showed before this list existed: everything, with Admin on the platform role, so
 * that a fault in the list never locks anyone out of the product.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { usePermissions } from './usePermissions';

export const ACCESS_KEY = ['access', 'me'] as const;

export type Permission =
  | 'platform.admin'
  | 'platform.library'
  | 'org.view'
  | 'org.manage'
  | 'org.join'
  | 'projects.view'
  | 'projects.create'
  | 'projects.admin'
  | 'orion.use'
  | 'runs.use'
  | 'confy.use'
  | 'insight.use'
  | 'rhino.use'
  | 'data.sync'
  | 'reports.view'
  | 'library.view'
  | 'library.edit'
  | 'credits.view'
  | 'issues.use'
  | 'profile.edit';

export interface ProjectAccess {
  id: number;
  code: string;
  name: string;
  role: 'ADMIN' | 'MEMBER';
  rhino: boolean;
  rhino_dml: boolean;
}

export interface Access {
  email: string;
  permissions: Permission[];
  platform_roles: string[];
  is_platform_admin: boolean;
  organization: {
    id: number;
    slug: string;
    name: string;
    status: string;
    role: 'ADMIN' | 'MEMBER';
    /** The company's plan: TRIAL | ACTIVE | PAST_DUE | CANCELLED | NONE | EXPIRED. */
    plan_status: string;
    plan_name: string | null;
    days_left: number | null;
    /** False when the plan allows no new work (a trial that ran out, a cancelled plan). */
    plan_usable: boolean;
  } | null;
  projects: ProjectAccess[];
  every_project: boolean;
}

export interface AccessValue {
  access: Access | undefined;
  isLoading: boolean;
  /** The list could not be fetched; `can` answers as the product did before the list. */
  degraded: boolean;
  can: (permission: Permission) => boolean;
  /** My role on a project, or null when I am not its member (platform staff administer all). */
  projectRole: (projectId: number | undefined) => 'ADMIN' | 'MEMBER' | null;
}

export function useAccess(): AccessValue {
  const { isAdmin } = usePermissions();
  const query = useQuery<Access>({
    queryKey: ACCESS_KEY,
    queryFn: () => api.get<Access>('/access/me'),
    staleTime: 60_000,
    retry: 1,
  });
  const access = query.data;
  const degraded = query.isError;

  const can = (permission: Permission): boolean => {
    if (access) return access.permissions.includes(permission);
    if (degraded) return permission === 'platform.admin' ? isAdmin : true;
    return false;
  };
  const projectRole = (projectId: number | undefined) => {
    if (projectId == null) return null;
    if (access?.every_project) return 'ADMIN';
    return access?.projects.find((p) => p.id === projectId)?.role ?? null;
  };

  return { access, isLoading: query.isLoading, degraded, can, projectRole };
}

/** Membership changed (a project member added, a company joined): fetch the list again. */
export function useRefreshAccess() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ACCESS_KEY });
}
